import { ChevronDown, ChevronUp } from 'lucide-react';
import { useMemo, useState } from 'react';

import { parseRows } from '../lib/parse-rows';
import { CopyButton } from './CopyButton';

interface DataTableProps {
  content: string;
  metadata?: unknown;
}

type Row = Record<string, string>;

interface SortState {
  col: string;
  dir: 'asc' | 'desc';
}

function compareValues(a: string | undefined, b: string | undefined, dir: 'asc' | 'desc'): number {
  const va = a ?? '';
  const vb = b ?? '';
  const numA = parseFloat(va);
  const numB = parseFloat(vb);

  let cmp: number;
  if (!isNaN(numA) && !isNaN(numB)) {
    cmp = numA - numB;
  } else {
    cmp = va.localeCompare(vb);
  }

  return dir === 'desc' ? -cmp : cmp;
}

function toCsv(columns: string[], rows: Row[]): string {
  const header = columns.map((c) => `"${String(c).replace(/"/g, '""')}"`).join(',');
  const body = rows.map((row) =>
    columns
      .map((col) => {
        const val = String(row[col] ?? '');
        return `"${val.replace(/"/g, '""')}"`;
      })
      .join(','),
  );
  return [header, ...body].join('\n');
}

function getTotalRows(metadata: unknown): number | null {
  if (
    metadata &&
    typeof metadata === 'object' &&
    'totalRows' in metadata &&
    typeof (metadata as { totalRows: unknown }).totalRows === 'number'
  ) {
    return (metadata as { totalRows: number }).totalRows;
  }
  return null;
}

export function DataTable({ content, metadata }: DataTableProps): React.ReactElement {
  const rows = useMemo(() => parseRows(content), [content]);
  const [sort, setSort] = useState<SortState | null>(null);

  const title =
    metadata && typeof metadata === 'object' && 'title' in metadata ? (metadata as { title: string }).title : null;

  const totalRows = getTotalRows(metadata);

  const columns = rows.length > 0 ? Object.keys(rows[0]!) : [];

  const sortedRows = useMemo(() => {
    if (!sort) return rows;
    return [...rows].sort((a, b) => compareValues(a[sort.col], b[sort.col], sort.dir));
  }, [rows, sort]);

  if (rows.length === 0) {
    return <pre className="overflow-x-auto text-xs text-muted-foreground">{content}</pre>;
  }

  const handleSort = (col: string): void => {
    setSort((prev) => {
      if (prev?.col === col) {
        return prev.dir === 'asc' ? { col, dir: 'desc' } : null;
      }
      return { col, dir: 'asc' };
    });
  };

  const csvText = toCsv(columns, sortedRows);

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        {title ? <div className="text-sm font-medium text-muted-foreground">{title}</div> : <span />}
        <CopyButton text={csvText} className="text-[0.625rem]" />
      </div>
      <div className="overflow-x-auto">
        <table className="w-full border-collapse border border-border text-sm">
          <thead>
            <tr>
              {columns.map((col) => (
                <th
                  key={col}
                  role="columnheader"
                  tabIndex={0}
                  aria-sort={sort?.col === col ? (sort.dir === 'asc' ? 'ascending' : 'descending') : undefined}
                  className="cursor-pointer border border-border bg-card px-3 py-2 text-left text-xs font-medium text-muted-foreground select-none hover:text-foreground focus:outline-2 focus:outline-brand"
                  onClick={() => handleSort(col)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleSort(col);
                    }
                  }}
                >
                  <span className="flex items-center gap-1">
                    {col}
                    {sort?.col === col &&
                      (sort.dir === 'asc' ? (
                        <ChevronUp size={12} aria-hidden="true" data-testid="sort-asc" />
                      ) : (
                        <ChevronDown size={12} aria-hidden="true" data-testid="sort-desc" />
                      ))}
                  </span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedRows.map((row, i) => (
              <tr key={i} className="transition-colors hover:bg-card/60">
                {columns.map((col) => (
                  <td key={col} className="border border-border px-3 py-1.5 text-foreground">
                    {row[col] ?? '–'}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="mt-1.5 text-xs text-muted-foreground" data-testid="row-count">
        {totalRows && totalRows > rows.length
          ? `showing ${rows.length} of ${totalRows} rows`
          : `${rows.length} ${rows.length === 1 ? 'row' : 'rows'}`}
      </div>
    </div>
  );
}
